import React from 'react'
import { concatStyles } from 'utils'
import * as Content from '../Content'

const CONTAINER_STYLES = 'flex w-full m-0 box-border'
const AVAILABLE_PAGE_STYLES =
  'text-actionable hover:bg-actionable hover:bg-opacity-25 rounded'
const PAGINATION_WRAPPER_STYLES = 'flex box-border p-2 m-0 mx-auto my-0'
const PAGE_STYLES = 'p-2 m-0 box-border'
const CURRENT_PAGE_STYLES = 'bg-actionable text-white rounded'
const TRUNCATION_STYLES = 'my-auto flex opacity-25'

export type PageHandlerFn = (page: number) => void

export interface SimplePaginationProps {
  currentPage: number
  onPageClick: PageHandlerFn
  pageCount?: number
  maxPages?: number
}

export type PaginationBarTruncationStyle =
  | 'intermediary'
  | 'head'
  | 'tail'
  | 'none'

export interface PageNumberProps {
  page: number
  onClick: PageHandlerFn
}

const PageNumber: React.FC<PageNumberProps> = ({
  page,
  onClick: handlePage,
}) => {
  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()
    handlePage(page)
  }

  return (
    <li className="flex" key={`pageButton${page}`}>
      <button
        className={concatStyles([PAGE_STYLES, AVAILABLE_PAGE_STYLES])}
        onClick={handleClick}
      >
        {page}
      </button>
    </li>
  )
}

/**
 * A traditional pagination component that uses an arbitrary page
 * count current page. Unlike the PaginationBar component which
 * relies on a GraphQL connection to specify surrounding cursors,
 * this component just uses props to specify a page range.
 * @param props The configuration for this component.
 */
export const SimplePaginationBar: React.FC<SimplePaginationProps> = ({
  currentPage,
  pageCount = 0,
  onPageClick,
  maxPages = 7,
}) => {
  if (pageCount <= 1) {
    return <React.Fragment />
  }

  const truncationStyle: PaginationBarTruncationStyle =
    pageCount < maxPages
      ? 'none'
      : currentPage > Math.ceil(maxPages / 2) + 1 &&
        currentPage < pageCount - maxPages / 2 - 1
      ? 'intermediary'
      : currentPage < maxPages
      ? 'head'
      : 'tail'

  const pages = new Array(pageCount)
    .fill(0)
    .map((_, i) => i + 1)
    .filter((pageNumber) => {
      switch (truncationStyle) {
        case 'head':
          return pageNumber < maxPages
        case 'tail':
          return pageNumber > pageCount - maxPages
        case 'intermediary':
          return (
            pageNumber > currentPage - maxPages / 2 &&
            pageNumber < currentPage + maxPages / 2
          )
        default:
          return true
      }
    })

  const previousPages = pages.filter((i) => i < currentPage)
  const nextPages = pages.filter((i) => i > currentPage)

  return (
    <Content.Text>
      <div className={CONTAINER_STYLES}>
        <ul className={PAGINATION_WRAPPER_STYLES}>
          {['tail', 'intermediary'].includes(truncationStyle) ? (
            <>
              <PageNumber page={1} onClick={onPageClick} />
              <span className={TRUNCATION_STYLES}>...</span>
            </>
          ) : null}
          {previousPages.map((page) => (
            <PageNumber page={page} onClick={onPageClick} />
          ))}
          <li className={concatStyles([PAGE_STYLES, CURRENT_PAGE_STYLES])}>
            {currentPage}
          </li>
          {nextPages.map((page) => (
            <PageNumber page={page} onClick={onPageClick} />
          ))}
          {['head', 'intermediary'].includes(truncationStyle) ? (
            <>
              <span className={TRUNCATION_STYLES}>...</span>
              <PageNumber page={pageCount} onClick={onPageClick} />
            </>
          ) : null}
        </ul>
      </div>
    </Content.Text>
  )
}
